// UI-FOUNDATION-1 (2026-06-17) — shared, opt-in UI primitives for AtticusNW.
// Thin wrappers over the styles.js token/utility layer so screens stop
// re-implementing pills, KPI cards, empty/loading/error states, page headers,
// and money cells inline. Adopt incrementally; nothing here is required.
//
// Semantic color map (single source of truth — see plans/ATTICUS_UI_ARCHITECTURE_SPRINT_1.md):
//   green = ready / paid / reconciled / approved / completed
//   amber = attention / aging / review-needed / hold / pending
//   red   = blocked / void / denied / rejected / negative / urgent
//   blue  = waiting / informational / synced / external process
//   slate = neutral / inactive / deferred / metadata
import { fmt } from "../api";

const TONE_BY_STATUS = {
  paid: "green", ready: "green", "claim ready": "green", reconciled: "green",
  approved: "green", complete: "green", completed: "green", active: "green", received: "green",
  sent: "blue", synced: "blue", queued: "blue", waiting: "blue", external: "blue", routed: "blue",
  // open = GREEN: matches the legacy statusColor language on the claim header (LD-DESIGN-004 —
  // the live palette wins; an open claim reads healthy/active, not informational).
  open: "green", "in progress": "blue", investigating: "blue", "pending info": "amber", "pending review": "amber",
  pending: "amber", "needs review": "amber", "needs coding": "amber", "needs claim": "amber",
  hold: "amber", "on hold": "amber", "request info": "amber", "adjuster review": "amber",
  "accounting review": "amber", aging: "amber", "partially ready": "amber", partial: "amber",
  void: "red", voided: "red", denied: "red", rejected: "red", blocked: "red", overdue: "red", urgent: "red",
  uncoded: "amber", imported: "slate", coded: "green",
  prepared: "blue", issued: "blue", "partially paid": "amber", disputed: "red", "written off": "slate",
  "partially received": "amber", received: "green", closed: "slate",
  draft: "slate", deferred: "slate", inactive: "slate", "not required": "slate",
};

export function toneForStatus(s) {
  return TONE_BY_STATUS[String(s || "").trim().toLowerCase().replace(/_/g, " ")] || "slate";
}

// StatusPill — colored status chip. Pass an explicit `tone`, or a `status`
// string that maps via the semantic table. `sm` = compact uppercase variant.
export function StatusPill({ children, status, tone, sm, title, style }) {
  const label = children != null ? children : status;
  const t = tone || toneForStatus(status != null ? status : children);
  return (
    <span className={`pill pill-${t}${sm ? " pill-sm" : ""}`} title={title} style={style}>
      {label}
    </span>
  );
}

// KpiBand + Kpi — consistent headline metric row (wraps the .stat-card system).
export function KpiBand({ children, style }) {
  return <div className="kpi-band" style={style}>{children}</div>;
}
export function Kpi({ label, value, sub, tone }) {
  return (
    <div className={`stat-card${tone ? ` ${tone}` : ""}`}>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {sub != null && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

// EmptyState — icon + title + hint + optional action. Falls back to plain text.
export function EmptyState({ icon = "📋", title, hint, action }) {
  return (
    <div className="empty-state">
      {icon && <div className="empty-icon">{icon}</div>}
      {title && <div className="empty-title">{title}</div>}
      {hint && <div className="empty-hint">{hint}</div>}
      {action && <div className="empty-action">{action}</div>}
    </div>
  );
}

export function Spinner({ label = "Loading…" }) {
  return <div className="loading"><span className="spinner-dot" /> <span style={{ marginLeft: 8 }}>{label}</span></div>;
}

export function ErrorBanner({ children }) {
  return <div className="alert-error">{children}</div>;
}

// PageHeader — title/subtitle on the left, actions on the right.
export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="page-header">
      <div>
        <div className="page-title">{title}</div>
        {subtitle && <div className="page-subtitle">{subtitle}</div>}
      </div>
      {actions && <div className="page-header-actions">{actions}</div>}
    </div>
  );
}

// Money — right-aligned, tabular, negative-aware money cell content.
export function Money({ value, className = "" }) {
  const n = Number(value) || 0;
  return <span className={`num${n < 0 ? " neg-money" : ""}${className ? " " + className : ""}`}>{fmt.money(value)}</span>;
}

// ============================================================================
// UI-STANDARD-1 (2026-07-02) — kit primitives per ATTICUS_DESIGN_STANDARD.md
// Part V. Extracted from their best live instances (ClaimWorkspace.js DashCard/
// KV/SubNav, ClaimFinancialsSummary cell pattern) so adoption is a code move,
// not a redesign — markup and pixel output are identical to the originals.
// ============================================================================

// OverviewTile — the cockpit summary block (LD-DESIGN-001): dark title-bar with
// an "Open →" deep-link into the owning tab, read-only body. Tiles orient; tabs
// work. Never put a form or workflow action inside one.
export function OverviewTile({ title, onOpen, accent, minHeight = 190, style, children }) {
  return (
    <div className="card" style={{ overflow: "hidden", display: "flex", flexDirection: "column", minHeight, ...(style || {}) }}>
      <div onClick={onOpen} title={onOpen ? "Open" : undefined}
        style={{ background: accent || "var(--navy)", color: "#fff", padding: "7px 12px", display: "flex",
                 alignItems: "center", justifyContent: "space-between", cursor: onOpen ? "pointer" : "default", userSelect: "none" }}>
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase" }}>{title}</span>
        {onOpen && <span style={{ fontSize: 11, opacity: 0.85, whiteSpace: "nowrap" }}>Open →</span>}
      </div>
      <div style={{ padding: "8px 12px", flex: 1 }}>{children}</div>
    </div>
  );
}

// KeyValue — the tile/preview label:value row. Empty values render an em-dash.
export function KeyValue({ label, children }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "3px 0", fontSize: 12.5, lineHeight: 1.45 }}>
      <span style={{ color: "var(--text3)", whiteSpace: "nowrap", flexShrink: 0 }}>{label}</span>
      <span style={{ fontWeight: 500, textAlign: "right", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
        {(children == null || children === "") ? <span style={{ color: "var(--text3)" }}>—</span> : children}
      </span>
    </div>
  );
}

// SummaryRibbon / RibbonCell — the page summary bar (Design Standard Part V):
// 4–8 metric cells before detail. Bordered strip, mono values, divider between
// cells. Extracted from ClaimFinancialsSummary (the financial trio strip).
// RibbonCell v2 (LD-DESIGN-010): optional icon (left, brand-colored) + label +
// mono value + optional sub(text). v1 calls (label/value/color) render unchanged.
export function RibbonCell({ label, value, color, sub, icon, iconColor, minWidth = 118 }) {
  const body = (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 9.5, fontWeight: 600, letterSpacing: 0.3, textTransform: "uppercase", color: "var(--text3)", whiteSpace: "nowrap" }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 700, fontFamily: "JetBrains Mono,monospace", marginTop: 1, color: color || "inherit", whiteSpace: "nowrap" }}>
        {value == null || value === "" ? "—" : value}
      </div>
      {sub != null && sub !== "" && (
        <div style={{ fontSize: 9.5, color: "var(--text3)", marginTop: 1, whiteSpace: "nowrap" }}>{sub}</div>
      )}
    </div>
  );
  return (
    <div style={{ padding: "6px 15px", minWidth, display: "flex", alignItems: "center", gap: 9 }}>
      {icon && <span style={{ fontSize: 16, lineHeight: 1, color: iconColor || "var(--blue)", flexShrink: 0 }}>{icon}</span>}
      {body}
    </div>
  );
}
export function RibbonDivider() {
  return <div style={{ width: 1, alignSelf: "stretch", background: "var(--border)" }} />;
}
// Children are RibbonCells; dividers are inserted automatically between them.
export function SummaryRibbon({ children, style }) {
  const kids = (Array.isArray(children) ? children : [children]).filter(Boolean).flat().filter(Boolean);
  return (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "stretch", marginBottom: 14, border: "1px solid var(--border)",
                  borderRadius: 8, overflow: "hidden", background: "#fff", maxWidth: "100%", ...(style || {}) }}>
      {kids.map((c, i) => [i > 0 ? <RibbonDivider key={`d${i}`} /> : null, c])}
    </div>
  );
}

// SubNav — pill sub-navigation inside a workspace tab (Financials sub-tabs etc.).
export function SubNav({ value, onChange, options }) {
  return (
    <div style={{ display: "inline-flex", gap: 2, marginBottom: 16, padding: 3, background: "#eef1f6", borderRadius: 8, maxWidth: "100%", flexWrap: "wrap" }}>
      {options.map(([id, label]) => (
        <button key={id} onClick={() => onChange(id)}
          style={{
            padding: "5px 14px", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12.5,
            fontWeight: value === id ? 600 : 500,
            color: value === id ? "var(--navy)" : "var(--text2)",
            background: value === id ? "#fff" : "transparent",
            boxShadow: value === id ? "0 1px 2px rgba(15,23,42,0.10)" : "none",
            transition: "background 0.12s",
          }}>
          {label}
        </button>
      ))}
    </div>
  );
}

// SectionHeader — title + optional subtitle on the left, actions on the right.
// (In-page section version of PageHeader; wraps the .section-header classes.)
export function SectionHeader({ title, subtitle, actions }) {
  return (
    <div className="section-header">
      <div>
        <div className="section-title">{title}</div>
        {subtitle && <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 1 }}>{subtitle}</div>}
      </div>
      {actions && <div style={{ display: "flex", gap: 8, alignItems: "center" }}>{actions}</div>}
    </div>
  );
}

// FilterBar — filters above the collection they control (Law 6). Children are
// the filter controls; optional onReset renders the standard Reset affordance.
export function FilterBar({ children, onReset, style }) {
  return (
    <div className="search-bar" style={style}>
      {children}
      {onReset && <button className="btn btn-outline btn-sm" onClick={onReset}>Reset</button>}
    </div>
  );
}

// TableShell — the enterprise-table wrapper: horizontal scroll + optional
// sticky header (list-view pattern from styles.js). Table markup stays native
// (global th/td styling applies); this standardizes the container.
export function TableShell({ children, sticky, compact, style }) {
  return (
    <div className={`table-wrap${sticky ? " list-view" : ""}`} style={style}>
      <div className={sticky ? "list-body" : undefined} style={sticky ? undefined : { display: "contents" }}>
        <table className={compact ? "tbl-compact" : undefined}>{children}</table>
      </div>
    </div>
  );
}
